/**
 * Shopping Preferences Manager
 *
 * High-level business logic for managing shopping preferences.
 * Pure functions that orchestrate storage, sync, and validation.
 */

import {
  ShoppingPreferences,
  PreferencesWithoutTimestamp,
  StorageSource,
  SyncResult,
  StorageToClear,
} from '@lib/shopping-preferences/types';
import { withTimestamp, createDefaultPreferences } from '@helpers/shopping-preferences/defaults';
import { validatePreferencesPayload, hasUserSelectedPreferences } from '@helpers/shopping-preferences/validation';
import { updateHomepageWithPreferences } from '@helpers/shopping-preferences/homepage-preferences-manager';
import logger from '@helpers/utils/logger/client';
import { syncPreferences, persistPreferences, clearPersistedPreferences } from './sync-strategy';

/**
 * Initialize preferences on app load
 *
 * @param backendPrefs - Preferences from backend API (if authenticated)
 * @param isAuthenticated - Whether user is authenticated
 * @param clearTemporaryOnSync - Force clear temporary storage (e.g., on login)
 * @returns Initial preferences and their source
 */
export const initializePreferences = (
  backendPrefs: PreferencesWithoutTimestamp | null,
  isAuthenticated: boolean,
  clearTemporaryOnSync: boolean = false,
): SyncResult => {
  logger.info('[Shopping Prefs Manager] Initializing preferences', {
    hasBackendPrefs: !!backendPrefs,
    isAuthenticated,
    clearTemporaryOnSync,
  });

  return syncPreferences(backendPrefs, isAuthenticated, clearTemporaryOnSync);
};

/**
 * Update preferences with full sync
 *
 * @param newPrefs - New preference values
 * @param currentPrefs - Current preferences
 * @param isAuthenticated - Whether user is authenticated
 * @returns Updated preferences with timestamp
 */
export const updatePreferencesWithSync = (
  newPrefs: PreferencesWithoutTimestamp,
  currentPrefs: ShoppingPreferences | null,
  isAuthenticated: boolean,
): { preferences: ShoppingPreferences; shouldSyncBackend: boolean } => {
  // Validate new preferences
  const validation = validatePreferencesPayload(newPrefs);
  if (!validation.isValid) {
    logger.error('[Shopping Prefs Manager] Invalid preferences update', validation.errors);
    throw new Error(`Invalid preferences: ${validation.errors?.join(', ')}`);
  }

  // Create updated preferences with timestamp
  const updated = withTimestamp(newPrefs);

  // Persist to storage
  const persistSuccess = persistPreferences(updated, isAuthenticated, false);
  if (!persistSuccess) {
    logger.error('[Shopping Prefs Manager] Failed to persist preferences');
    throw new Error('Failed to persist preferences to storage');
  }

  logger.info('[Shopping Prefs Manager] Preferences updated successfully', {
    universe: updated.universe,
    isAuthenticated,
  });

  return {
    preferences: updated,
    shouldSyncBackend: isAuthenticated,
  };
};

/**
 * Set session-only preferences (temporary, not persisted)
 *
 * @param newPrefs - New preference values
 * @returns Updated preferences with timestamp
 */
export const setSessionOnlyPreferences = (newPrefs: PreferencesWithoutTimestamp): ShoppingPreferences => {
  // Validate new preferences
  const validation = validatePreferencesPayload(newPrefs);
  if (!validation.isValid) {
    logger.error('[Shopping Prefs Manager] Invalid session preferences', validation.errors);
    throw new Error(`Invalid preferences: ${validation.errors?.join(', ')}`);
  }

  // Create updated preferences with timestamp
  const updated = withTimestamp(newPrefs);

  // Persist to session only
  const persistSuccess = persistPreferences(updated, false, true);
  if (!persistSuccess) {
    logger.error('[Shopping Prefs Manager] Failed to persist session preferences');
    throw new Error('Failed to persist session preferences');
  }

  // Update homepage if needed
  updateHomepageWithPreferences(updated);

  logger.info('[Shopping Prefs Manager] Session preferences set', {
    universe: updated.universe,
  });

  return updated;
};

/**
 * Clear preferences from storage
 *
 * @param storage - Which storage to clear
 * @returns Default preferences after clearing
 */
export const clearPreferences = (storage: StorageToClear): ShoppingPreferences => {
  logger.info('[Shopping Prefs Manager] Clearing preferences', { storage });

  clearPersistedPreferences(storage);

  // Return defaults
  return createDefaultPreferences();
};

/**
 * Check if user has made preference selections
 *
 * @param preferences - Current preferences
 * @returns Whether user has selected preferences
 */
export const checkUserSelection = (preferences: ShoppingPreferences | null): boolean => {
  return hasUserSelectedPreferences(preferences);
};

/**
 * Determine if modal should be shown
 *
 * @param preferences - Current preferences
 * @param source - Source of current preferences
 * @param isEnabled - Whether feature is enabled
 * @param pathname - Current pathname
 * @returns Whether modal should be shown
 */
export const shouldShowPreferencesModal = (
  preferences: ShoppingPreferences | null,
  source: StorageSource,
  isEnabled: boolean,
  pathname: string,
): boolean => {
  // Don't show if feature disabled
  if (!isEnabled) {
    return false;
  }

  // Only show on homepage
  if (pathname !== '/') {
    return false;
  }

  // Don't show for temporary preferences (session-only)
  if (preferences?.storage_type === 'temporary') {
    return false;
  }

  // Show if user hasn't selected preferences yet
  return !hasUserSelectedPreferences(preferences);
};
